interface Field {
  label: string;
  key: string;
  alternateMatches?: string[];
  fieldType: {
    type: "input" | "checkbox" | "select";
    options?: any;
  };
  select?: { [key: string]: string };
  example?: string;
  validations?: Validation[];
}

interface Validation {
  rule: "required" | "unique" | "regex";
  regex?: RegExp;
  errorMessage: string;
  level?: "info" | "warning" | "error";
}

const getPackingFields: (packingListConfig: any, SelectOptions: any) => Field[] = (packingListConfig, selectOptions) => {
  if (!packingListConfig) return [];
  const {
    crating_required_enabled,
    dangerous_goods_enabled,
    hazardous_goods_enabled,
    temperature_controlled_enabled,
    customs_value_setting,
  } = packingListConfig;

  const packingListFields: any = [
    {
      label: "Handling Unit",
      key: "handling_unit",
      alternateMatches: ["commodity type", "commodity", "handling unit", "handling"],
      fieldType: {
        type: "select",
        options: selectOptions.handlingUnits,
      },
      example: "Pallet",
      validations: [
        {
          rule: "required",
          errorMessage: "Commodity type is required",
          level: "error",
        },
      ],
    },
    {
      label: "Container type",
      key: "container_unit",
      alternateMatches: ["container", "container type"],
      fieldType: {
        type: "select",
        options: selectOptions.allHandlingUnitOptions,
      },
      example: "20ft Standard",
    },
    {
      label: "Length",
      key: "length",
      fieldType: {
        type: "input",
      },
      example: "20",
    },
    {
      label: "Width",
      key: "width",
      fieldType: {
        type: "input",
      },
      example: "10",
    },
    {
      label: "Height",
      key: "height",
      fieldType: {
        type: "input",
      },
      example: "30",
    },
    {
      label: "Units",
      key: "units",
      fieldType: {
        type: "input",
      },
      example: "40",
      alternateMatches: ["Number of units", "units"],
      validations: [
        {
          rule: "required",
          errorMessage: "Units is required",
          level: "error",
        },
      ],
    },
    {
      label: "Weight per Piece",
      key: "weight_piece",
      fieldType: {
        type: "input",
      },
      alternateMatches: ["weight", "weight per piece", "weight per unit", "piece weight", "unit weight"],
      example: "10",
    },
    {
      label: "NMFC Class",
      key: "nmfc_class",
      fieldType: {
        type: "select",
        options: selectOptions.nmfcOptions,
      },
      example: "70",
      ...(packingListConfig.nmfc_number_enabled === "Mandatory"
        ? {
            validations: [
              {
                rule: "required",
                errorMessage: "NMFC Class is required",
                level: "error",
              },
            ],
          }
        : {}),
      alternateMatches: ["NMFC Class", "class", "nmfc class", "nmfc", "nmfc class"],
    },
    {
      label: "Stackable",
      key: "stackable",
      alternateMatches: ["stackable", "stack", "stacked"],
      fieldType: {
        type: "checkbox",
      },
    },
  ];

  if (crating_required_enabled) {
    packingListFields.push({
      label: "Crating Required By",
      key: "crating_required_by",
      alternateMatches: ["crating", "crating required", "crating required by"],
      fieldType: {
        type: "input",
      },
    });
  }
  if (dangerous_goods_enabled) {
    packingListFields.push({
      label: "Dangerous Good",
      key: "dangerous_good_value",
      alternateMatches: ["danger", "dangerous", "danger goods", "dangerous goods"],
      fieldType: {
        type: "select",
        options: selectOptions.dangerousGoods,
      },
    });
  }
  if (temperature_controlled_enabled) {
    packingListFields.push({
      label: "Temperature Control",
      key: "temperature_control_value",
      alternateMatches: [
        "temp",
        "temperature",
        "celcius",
        "fahrenheit",
        "temp control",
        "temperature control",
        "controlled",
        "temperature controlled",
      ],
      fieldType: {
        type: "select",
        options: selectOptions.temperatureControl,
      },
    });
  }

  if (hazardous_goods_enabled) {
    packingListFields.push({
      label: "Hazardous",
      key: "hazardous",
      alternateMatches: ["hazardous", "hazmat", "hazardous material", "hazardous materials", "hazard"],
      fieldType: {
        type: "checkbox",
      },
    });
  }

  if (customs_value_setting !== "Off") {
    packingListFields.push(
      {
        label: "HS Code",
        key: "hs_code",
        alternateMatches: ["HS", "HS Code", "Customs code"],
        fieldType: {
          type: "input",
        },
        ...(customs_value_setting === "Mandatory"
          ? {
              validations: [
                {
                  rule: "required",
                  errorMessage: "HS Code is required",
                  level: "error",
                },
              ],
            }
          : {}),
      },
      {
        label: "Value",
        key: "value",
        alternateMatches: ["Value", "customs value"],
        fieldType: {
          type: "input",
        },
        ...(customs_value_setting === "Mandatory"
          ? {
              validations: [
                {
                  rule: "required",
                  errorMessage: "Value is required",
                  level: "error",
                },
              ],
            }
          : {}),
      },
      {
        label: "Currency",
        key: "currency_code",
        alternateMatches: ["Currency", "currency", "currency code"],
        fieldType: {
          type: "select",
          options: selectOptions.currencyOptions,
        },
        ...(customs_value_setting === "Mandatory"
          ? {
              validations: [
                {
                  rule: "required",
                  errorMessage: "Currency is required",
                  level: "error",
                },
              ],
            }
          : {}),
      },
      {
        label: "Commodity information",
        key: "description",
        alternateMatches: ["description", "info", "information", "commodity information", "commodity info"],
        fieldType: {
          type: "input",
        },
        ...(customs_value_setting === "Mandatory"
          ? {
              validations: [
                {
                  rule: "required",
                  errorMessage: "Commodity information is required",
                  level: "error",
                },
              ],
            }
          : {}),
      },
    );
  }

  return packingListFields;
};

export default getPackingFields;
